/**
 * Lightweight YAML syntax tokenizer (issue #20).
 *
 * No external/CDN highlight library — a small line-based tokenizer used by
 * the YamlViewer component. Tokens carry Tailwind classes for coloring.
 */

export type Token = { text: string; className?: string }

/** Tokenize the value part of a `key: value` line. */
const tokenizeValue = (value: string): Token[] => {
  const trimmed = value.trim()
  if (trimmed === '') return [{ text: value }]

  // Inline comment split (naive: only when not inside quotes)
  const commentIdx = value.indexOf(' #')
  if (commentIdx >= 0 && !trimmed.startsWith('"') && !trimmed.startsWith("'")) {
    return [
      ...tokenizeValue(value.slice(0, commentIdx)),
      { text: value.slice(commentIdx), className: 'text-gray-500 italic' },
    ]
  }

  if (/^["'].*["']\s*$/.test(trimmed)) {
    return [{ text: value, className: 'text-amber-300' }]
  }
  if (/^(true|false|null|~)\s*$/i.test(trimmed)) {
    return [{ text: value, className: 'text-purple-300' }]
  }
  if (/^-?\d+(\.\d+)?\s*$/.test(trimmed)) {
    return [{ text: value, className: 'text-cyan-300' }]
  }
  return [{ text: value, className: 'text-emerald-300' }]
}

/** Tokenize one YAML line into highlighted spans. */
export const tokenizeYamlLine = (line: string): Token[] => {
  // Full-line comment
  if (/^\s*#/.test(line)) {
    return [{ text: line, className: 'text-gray-500 italic' }]
  }

  // List item "- value" or "- key: value"
  const listMatch = line.match(/^(\s*-\s+)(.*)$/)
  if (listMatch) {
    return [
      { text: listMatch[1], className: 'text-gray-400' },
      ...tokenizeYamlLine(listMatch[2]),
    ]
  }

  // "key: value" (key may be quoted or dotted)
  const keyMatch = line.match(/^(\s*)([\w.'"-]+)(\s*:)(\s?.*)$/)
  if (keyMatch) {
    const tokens: Token[] = [
      { text: keyMatch[1] },
      { text: keyMatch[2], className: 'text-sky-300 font-medium' },
      { text: keyMatch[3], className: 'text-gray-400' },
    ]
    if (keyMatch[4]) tokens.push(...tokenizeValue(keyMatch[4]))
    return tokens
  }

  return tokenizeValue(line)
}

